import { apiClient } from "../core/apiClient";

/**
 * Interfaz para los datos completos del perfil de usuario
 * Incluye información adicional como relaciones y roles
 */
export interface IUserProfile {
  id: string;
  nombre: string;
  apellido: string;
  email: string;
  rut: string;
  telefono?: string | null;
  activo: boolean;
  created_at: string;
  updated_at: string;
  roles: string[];
  hijos?: Array<{
    id: string;
    nombre: string;
    apellido: string;
    email: string;
    rut: string;
  }>;
}

/**
 * Interfaz para actualizar datos del perfil
 */
export interface IUpdateProfileData {
  nombre?: string;
  apellido?: string;
  email?: string;
  telefono?: string;
}

/**
 * Interfaz para cambiar contraseña
 */
export interface IChangePasswordData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

/**
 * Servicio para gestionar el perfil del usuario
 */
export class ProfileService {
  /**
   * Obtiene el perfil completo del usuario autenticado
   *
   * @returns Promise con los datos del perfil
   */
  static async getUserProfile(): Promise<IUserProfile> {
    try {
      const response = await apiClient.get("/api/v1/users/me");
      // El backend devuelve los datos en response.data.data
      return response.data.data;
    } catch (error: any) {
      if (error.response?.status === 401) {
        throw new Error("No autorizado. Por favor, inicia sesión nuevamente.");
      }
      if (error.response?.status === 404) {
        throw new Error("Perfil de usuario no encontrado.");
      }
      throw new Error(
        "Error al obtener el perfil: " +
          (error.response?.data?.message || error.message)
      );
    }
  }

  /**
   * Actualiza los datos básicos del perfil
   *
   * @param profileData - Datos a actualizar
   * @returns Promise con el perfil actualizado
   */
  static async updateProfile(
    profileData: IUpdateProfileData
  ): Promise<IUserProfile> {
    try {
      const response = await apiClient.put("/api/v1/users/me", profileData);
      return response.data.data;
    } catch (error: any) {
      if (error.response?.status === 400) {
        throw new Error(
          "Datos inválidos: " +
            (error.response?.data?.message ||
              "Verifica la información ingresada")
        );
      }
      if (error.response?.status === 409) {
        throw new Error("El email ya está en uso por otro usuario.");
      }
      throw new Error(
        "Error al actualizar el perfil: " +
          (error.response?.data?.message || error.message)
      );
    }
  }

  /**
   * Cambia la contraseña del usuario
   *
   * @param passwordData - Datos de cambio de contraseña
   * @returns Promise<{success: boolean, message: string}>
   */
  static async changePassword(
    passwordData: IChangePasswordData
  ): Promise<{success: boolean, message: string}> {
    try {
      const response = await apiClient.post("/api/v1/users/me/change-password", passwordData);
      return {
        success: true,
        message: response.data?.message || "Contraseña cambiada exitosamente"
      };
    } catch (error: any) {
      if (error.response?.status === 400) {
        throw new Error(
          error.response?.data?.message || "Contraseña actual incorrecta o nueva contraseña inválida."
        );
      }
      throw new Error(
        "Error al cambiar la contraseña: " +
          (error.response?.data?.message || error.message)
      );
    }
  }

  /**
   * Desactiva la cuenta del usuario
   *
   * @returns Promise<void>
   */
  static async deactivateAccount(): Promise<void> {
    try {
      await apiClient.post("/api/v1/users/me/deactivate");
    } catch (error: any) {
      throw new Error(
        "Error al desactivar la cuenta: " +
          (error.response?.data?.message || error.message)
      );
    }
  }
}

export default ProfileService;
